import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import IconButton from '../components/IconButton';
import DateInput from '../components/DateInput';
import { useAuth } from '../context/AuthContext';

export default function ClientPaymentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [tab, setTab] = useState('pending'); // pending | completed | all
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(new Set()); // Set of selected task ids for invoice generation
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [taskCategory, setTaskCategory] = useState('WO');
  const { user } = useAuth();
  

  // Permissions
    const isAdmin = user?.role_obj?.name?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'admin';
    

  const fetchClient = async () => {
    try {
      const res = await api.get(`/clients/${id}`);
      setClient(res.data.data);
    } catch (err) {
      setClient(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { clientId: id };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (tab !== 'all') params.status = tab;
      if (taskCategory !== 'all') params.taskCategory = taskCategory;

      const res = await api.get('/reports/client-payments', { params });
      setSummary(res.data.summary || { pendingAmount: 0, completedAmount: 0, pendingCount: 0, completedCount: 0 });
      setPayments(res.data.payments || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load payments');
      setSummary(null);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClient(); }, [id]);
  useEffect(() => { fetchData(); }, [id, fromDate, toDate, tab, taskCategory]);

  // Clear selected when tab or payments change
  useEffect(() => { setSelected(new Set()); }, [tab, payments]);

  const markAsPaid = async (taskId) => {
    if (!window.confirm('Mark this task payment as paid?')) return;
    try {
      await api.put(`/tasks/${taskId}`, { is_payment_done: true });
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark paid');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === payments.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(payments.map(p => p.id)));
    }
  };

  const generateInvoices = () => {
    // Open modal to collect invoice number before generating
    if (selected.size === 0) return;
    setInvoiceNumber('');
    setInvoiceDate(new Date().toISOString().split('T')[0]); // default to today
    setShowInvoiceModal(true);
  };

  const submitInvoice = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      setShowInvoiceModal(false);
      setLoading(true);
      const res = await api.post('/reports/invoices', { taskIds: ids, invoiceNumber: invoiceNumber, invoiceDate: invoiceDate }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = invoiceNumber ? `invoice_${invoiceNumber}.pdf` : `invoices_${Date.now()}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // pass blob data to backend to store in invoice table
      const formData = new FormData();
      formData.append('pdf_link', blob, fileName);
      formData.append('client_id', id);
      formData.append('task_ids', JSON.stringify(ids));
      formData.append('invoice_number', invoiceNumber);
      formData.append('invoice_date', invoiceDate);
      const invoiceRes = await api.post('/invoices', formData, { headers: { 'Content-Type': 'multipart/form-data' } });  
      
      // const invoiceRes = await api.post('/invoices', { client_id: id, task_ids: ids, invoice_number: invoiceNumber, invoice_date: invoiceDate, pdf_link: res.data });

    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate invoices');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clients-container">
      <div className="table-header">
        <div>
          <h2>Payments{client ? ` — ${client.name}` : ''}</h2>
          <div style={{ marginTop: 6 }}>
            <button className="back-btn" onClick={() => navigate('/clients')}>Back to Clients</button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="modal-content-like" style={{ padding: '12px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="date-filter-group" style={{ minWidth: 180 }}>
              <DateInput label="From" value={fromDate} onChange={val => setFromDate(val)} />
            </div>
            <div className="date-filter-group" style={{ minWidth: 180 }}>
              <DateInput label="To" value={toDate} onChange={val => setToDate(val)} />
            </div>
          {/* create toggle button for WO/Minutes */}
            <div>
              <label>Payment Status:</label>
              <select value={tab} onChange={(e) => setTab(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="all">All</option>
              </select>
            </div>
            <div>
              <label>Task Type:</label>
              <select value={taskCategory} onChange={(e) => setTaskCategory(e.target.value)}>
                <option value="WO">WO</option>
                <option value="Minutes">Minutes</option>
              </select>
            </div>


            {/* <div className="tabs">
              <button className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>Pending</button>
              <button className={tab === 'completed' ? 'active' : ''} onClick={() => setTab('completed')}>Completed</button>
              <button className={tab === 'all' ? 'active' : ''} onClick={() => setTab('all')}>All</button>
            </div> */}
          </div>
          {/* <div>
            <button className="close-btn" onClick={() => navigate('/clients')}>Close</button>
          </div> */}
        </div>
        
        <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
          {isAdmin && (
            <><span className="summary-item"> <strong>Summary:</strong> </span>
              <span className="summary-item">Pending: <strong>₹ {summary?.pendingAmount?.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong> ({summary?.pendingCount})</span>
              <span className="summary-item">Completed: <strong>₹ {summary?.completedAmount?.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0})}</strong> ({summary?.completedCount})</span>
            </>
          )}
          

          {/* Invoice generation controls (visible in pending tab) */}
          {tab === 'pending' && (
            <div style={{ marginLeft: 'auto' }}>
              <button className="generate-btn" onClick={generateInvoices} disabled={selected.size === 0}>Generate Invoice (PDF)</button>
            </div>
          )}
        </div>

        {/* Invoice number modal */}
        {showInvoiceModal && (
          <div className="modal-overlay" style={{ backgroundColor:"#89c5987a", marginBottom:"15px"}}>
            <div className="modal" >
              {/* <div className="modal-header">
                <h3>Generate Invoice</h3>
                <button className="close-button" onClick={() => setShowInvoiceModal(false)}>&times;</button>
              </div> */}
              <div style={{ padding: 12 }}>
                <label> <i style={{color:"#dd0012", fontSize:"14px"}}>Invoice Number and Invoice Date fields are required </i></label>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Enter Invoice Number" style={{ width: '100%'}}  required/>
                  <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} placeholder="Enter Invoice Date" style={{ width: '100%'}}  required/>
                  <button className="cancel-btn" onClick={() => setShowInvoiceModal(false)}>Cancel</button>
                  <button className="generate-btn" onClick={submitInvoice} disabled={selected.size === 0 || !invoiceNumber || !invoiceDate}>Generate</button>
                </div>
              </div>
              
            </div>
          </div>
        )}

        <div className="table-container table-responsive">
          {loading ? null : (
            <table className="table">
              <thead>
                <tr>
                  {tab === 'pending' && (
                    <th style={{ width: 40 }}>
                      <input type="checkbox" onChange={toggleSelectAll} checked={payments.length > 0 && selected.size === payments.length} />
                    </th>
                  )}
                  <th>ID</th>
                  <th>Task</th>
                  <th>AssignedTo</th>
                  <th> Status </th>
                  {isAdmin && <th>Fee</th>}
                  <th>Created</th>
                  <th>Completed</th>
                  <th>InvoiceNumber</th>
                  <th>InvoiceFile</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={tab === 'pending' ? 12 :11}>No payments</td></tr>
                ) : payments.map(p => (
                  <tr key={p.id}>
                    {tab === 'pending' && (
                      <td><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} /></td>
                    )}
                    {/* Add hyperlink to the task */}
                    <td><a href={`/tasks/edit/${p.id}`}>{p.id}</a></td>
                    <td>{p.title}</td>
                    <td>{p.assigned_to_user ? `${p.assigned_to_user.firstName} ${p.assigned_to_user.lastName}` : '-'}</td>
                    <td>{p.Status?.name || '-'}</td>
                    {isAdmin && <td>₹ {p.fee?.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>}
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td>{p.completed_at ? new Date(p.completed_at).toLocaleDateString() : '-'}</td>
                    <td>{p?.invoice?.invoice_number || '-'}</td>
                    <td>{p?.invoice?.pdf_url ? <a href={p?.invoice?.pdf_url} target="_blank" rel="noopener noreferrer">Download</a> : '-'}</td>
                    <td>{p.is_payment_done ? 'Yes' : 'No'}</td>
                    <td>
                      {!p.is_payment_done && (
                        <button className="pay-btn" onClick={() => markAsPaid(p.id)}>Mark paid</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      <style jsx>{`
      .table-container { background: white; border-radius: 8px;box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);padding: 16px; }
        .clients-container { padding: 20px; max-width: 1200px; margin: 0 auto; }
        .table-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
        .back-btn { padding:8px 12px; border-radius:4px; background:#f4f5f7; }
        .summary-item { background:#f6f8fa; padding:10px 12px; border-radius:6px; }
        .tabs { display:flex; gap:8px; }
        .tabs button { padding:6px 10px; border-radius:6px; background: #2c3e51; border:1px solid #e0e0e0; cursor:pointer; }
        .tabs button.active { background: #1abc9d;color: #fff;border-color: #fff; }
        .date-filter-group { display: flex; gap: 8px; align-items: center; }
        .date-filter-group label { font-size: 14px; color: #6B778C; white-space: nowrap; }
        .date-input { padding: 8px 12px; border: 1px solid #e1e4e8; border-radius: 6px; font-size: 14px; transition: all 200ms ease; }
        .date-input:focus { outline: none; border-color: #0066cc; box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1); }
        .table { width:100%; border-collapse:collapse; }
        .table th, .table td { border:1px solid #eaeaea; padding:8px; text-align:left; }
        .pay-btn { background:#00875a; color:#fff; padding:6px 8px; border-radius:6px; }
        .generate-btn { background:#0057b7; color:#fff; padding:8px 10px; border-radius:6px; border:none; cursor:pointer; }
        .generate-btn[disabled] { opacity:0.6; cursor:not-allowed; }
      `}</style>
    </div>
  );
}